import { FC, createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import './Cashier.css';
import { AuthContext } from '../api/authContext';
import { pushDisplayCart } from '../api/client';
import { EventContext } from '../api/eventContext';
import { CART_STORAGE_PREFIX } from '../offline/queue';
import { applyBundles } from '../shared/functions/bundles';
import { formatDollars } from '../shared/functions/formatter';
import useWindowDimensions from '../shared/hooks/useWindowDimensions';
import SelectedItems from './displaying/SelectedItems';
import TillOrderSheet from './displaying/TillOrderSheet';
import ItemSelector from './selecting/ItemSelector';

type CashierProps = {
  categoryItems: CategoryItem[];
  bundles: CatalogBundle[];
  /** True once the live catalogue has answered; see App. */
  catalogLoaded: boolean;
  /** True once that fetch has failed; the till says so when there is no cache. */
  catalogFailed: boolean;
};

/**
 * Cart actions.
 *
 * The cart holds catalog items only. Bundles are a *discount* derived from those
 * items, so there is no action to add or remove one: picking up the components
 * is what earns the saving.
 */
type CashierAction =
  | { type: 'ADD'; payload: Item }
  | { type: 'REMOVE'; payload: Item }
  | { type: 'RESET' }
  /** The record path's clear: it empties the same way but is NEVER undoable. */
  | { type: 'RECORDED' }
  | { type: 'RESTORE'; items: SelectedItem[] };

/** How long a manually cleared order can be brought back. */
const UNDO_VISIBLE_MS = 8000;

/** The phone layout's breakpoint; 620 = phone, 621 = tablet/desktop (spec 52). */
const COMPACT_MAX_WIDTH = 620;

const emptyState = (): CashierState => ({ items: [], bundles: [], total: 0, savings: 0 });

/** Build the cart view from its items; the bundle discounts are derived. */
function deriveState(items: SelectedItem[], catalog: CatalogBundle[]): CashierState {
  const { bundles, savings } = applyBundles(items, catalog);
  const itemsTotal = items.reduce((sum, item) => sum + item.subtotal, 0);
  return { items, bundles, total: itemsTotal - savings, savings };
}

const cartReducer = (items: SelectedItem[], action: CashierAction): SelectedItem[] => {
  if (action.type === 'ADD') {
    const payload = action.payload;
    const existing = items.find((item) => item.id === payload.id);
    if (existing) {
      return items.map((item) =>
        item.id === payload.id ? { ...item, quantity: item.quantity + 1, subtotal: item.subtotal + payload.price } : item
      );
    }
    return [...items, { ...payload, quantity: 1, subtotal: payload.price }];
  }
  if (action.type === 'REMOVE') {
    const payload = action.payload;
    return items.reduce<SelectedItem[]>((acc, item) => {
      if (item.id !== payload.id) {
        acc.push(item);
        return acc;
      }
      if (item.quantity > 1) {
        acc.push({ ...item, quantity: item.quantity - 1, subtotal: item.subtotal - payload.price });
      }
      return acc;
    }, []);
  }
  if (action.type === 'RESET' || action.type === 'RECORDED') {
    return [];
  }
  if (action.type === 'RESTORE') {
    return action.items;
  }
  return items;
};

/** Add an item quantity, used when folding a pre-auto-apply cart back together. */
function addItemQuantity(items: SelectedItem[], item: Item, quantity: number): void {
  const existing = items.find((entry) => entry.id === item.id);
  if (existing) {
    existing.quantity += quantity;
    existing.subtotal += item.price * quantity;
    return;
  }
  items.push({ ...item, quantity, subtotal: item.price * quantity });
}

/** The bundle shape an older cart wrote, before bundles were derived. */
type LegacyStoredBundle = {
  quantity?: number;
  components?: {
    item_id?: number | null;
    title?: string;
    price?: number;
    img?: string;
    quantity?: number;
  }[];
};

function readStoredItems(username: string | null): SelectedItem[] {
  if (!username) return [];
  try {
    const raw = localStorage.getItem(CART_STORAGE_PREFIX + '.' + username);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<CashierState>;
    if (!parsed || !Array.isArray(parsed.items)) return [];

    const items: SelectedItem[] = parsed.items.map((item) => ({ ...item }));

    /*
     * Carts written before bundles were derived stored the bundle as its own
     * line and left the components out of items. Fold those components back in
     * so the order survives the upgrade; the derived discount then re-applies
     * them automatically.
     */
    const legacy = (Array.isArray(parsed.bundles) ? parsed.bundles : []) as unknown as LegacyStoredBundle[];
    for (const bundle of legacy) {
      const count = typeof bundle.quantity === 'number' ? bundle.quantity : 1;
      for (const component of bundle.components ?? []) {
        // Legacy carts only ever held item components.
        if (component.item_id === null || component.item_id === undefined) continue;
        addItemQuantity(
          items,
          {
            id: component.item_id,
            title: component.title ?? '',
            price: component.price ?? 0,
            img: component.img ?? '',
          },
          (component.quantity ?? 1) * count
        );
      }
    }
    return items;
  } catch {
    return [];
  }
}

// Context is intentionally co-located with Cashier; splitting it out is a larger refactor.
// eslint-disable-next-line react-refresh/only-export-components
export const CashierContext = createContext<{
  state: CashierState;
  dispatch: React.Dispatch<CashierAction>;
  /**
   * Mark the order finished for the customer display. The cart still clears via
   * RESET; this is the explicit signal that distinguishes a recorded sale from a
   * cancelled order, so the display never thanks a customer for a RESET.
   */
  completeOrder: () => void;
  /**
   * A manual RESET is undoable for a few seconds; a recorded sale is not. The
   * snapshot lives with the till so the record path can drop it before it could
   * ever be applied.
   */
  canUndo: boolean;
  applyUndo: () => void;
}>({
  state: emptyState(),
  dispatch: () => null,
  completeOrder: () => null,
  canUndo: false,
  applyUndo: () => null,
});

const Cashier: FC<CashierProps> = ({ categoryItems, bundles: catalogBundles, catalogLoaded, catalogFailed }) => {
  const { username } = useContext(AuthContext);
  const { activeEvent } = useContext(EventContext);
  const [items, dispatchCart] = useReducer(cartReducer, username, readStoredItems);
  const state = useMemo(() => deriveState(items, catalogBundles), [items, catalogBundles]);
  /** Spec 29: the phone order sheet. Desktop always shows its own pane. */
  const [orderExpanded, setOrderExpanded] = useState(false);
  /** Set when a sale is recorded or queued; cleared when the next order starts. */
  const [orderComplete, setOrderComplete] = useState(false);
  /** The cart as it was before a manual RESET, while the undo window is open. */
  const [undo, setUndo] = useState<SelectedItem[] | null>(null);
  const { width } = useWindowDimensions();
  /** The one layout decision the whole phone till shares (spec 52). */
  const compact = width <= COMPACT_MAX_WIDTH;

  /**
   * A new line starts a new order, so the display stops being told the last one
   * finished. Clearing it in the action, not in an effect, keeps a state write
   * out of the render/effect path and is the only place it can safely happen.
   */
  const dispatch: React.Dispatch<CashierAction> = (action) => {
    if (action.type === 'ADD') {
      // A new line starts a new order, so the undo window closes with the old one.
      setUndo(null);
      if (orderComplete) setOrderComplete(false);
    }
    if (action.type === 'RESET' && items.length > 0) setUndo(items);
    // The record path clears through RECORDED, so a completed sale can never
    // leave an undo that would restore an order the customer already paid for.
    if (action.type === 'RECORDED') setUndo(null);
    dispatchCart(action);
  };

  const applyUndo = () => {
    if (undo === null) return;
    const snapshot = undo;
    setUndo(null);
    dispatchCart({ type: 'RESTORE', items: snapshot });
  };

  // The window closes on its own, so a cleared order cannot be resurrected later.
  useEffect(() => {
    if (undo === null) return undefined;
    const timer = window.setTimeout(() => setUndo(null), UNDO_VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [undo]);

  useEffect(() => {
    if (!username) return;
    try {
      localStorage.setItem(CART_STORAGE_PREFIX + '.' + username, JSON.stringify({ items }));
    } catch {
      // Storage full or unavailable — the order still works, it just will not
      // survive a reload. Not worth interrupting the till for.
    }
  }, [items, username]);

  // Mirror the basket to the customer display. Debounced so a burst of taps
  // sends one frame; the display only ever needs the latest total. The
  // completion flag rides the frame that cleared the cart, and only then.
  useEffect(() => {
    const payload = {
      items: state.items.map((item) => ({
        id: item.id,
        title: item.title,
        quantity: item.quantity,
        price: item.price,
        subtotal: item.subtotal,
      })),
      total: state.total,
      savings: state.savings,
      event_name: activeEvent?.name ?? null,
      order_complete: orderComplete && state.items.length === 0,
    };
    const timer = window.setTimeout(() => {
      pushDisplayCart(payload).catch(() => {
        // A failed publish must not disturb the till; the next change retries.
      });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [state, activeEvent, orderComplete]);

  const itemCount = state.items.length;
  const countLabel = itemCount === 1 ? '1 item' : itemCount + ' items';
  const gridClass = 'cashierGrid' + (compact ? ' cashierGridPhone' : '') + (compact && itemCount > 0 ? ' cashierGridHasCart' : '');

  return (
    <CashierContext.Provider value={{ state, dispatch, completeOrder: () => setOrderComplete(true), canUndo: undo !== null, applyUndo }}>
      <div className={gridClass}>
        <ItemSelector
          categoryItems={categoryItems}
          bundles={catalogBundles}
          catalogLoaded={catalogLoaded}
          catalogFailed={catalogFailed}
          compact={compact}
        />
        {compact ? (
          <>
            {/* Spec 23-27: the phone's persistent order summary. Hidden while the
                order is empty (spec 25B) so an inert "$0.00" never reads as a
                checkout. The whole surface is the tap target and the CSS reserves
                its height, so it covers neither the last product nor the actions. */}
            {itemCount > 0 && (
              <button
                type="button"
                className="cartBar"
                onClick={() => setOrderExpanded(true)}
                aria-label={`View order, ${countLabel}, ${formatDollars(state.total)}`}
              >
                <span className="cartBarSummary">
                  <span className="cartBarCount">{countLabel}</span>
                  <span className="cartBarTotal money">{formatDollars(state.total)}</span>
                </span>
                <span className="cartBarHint">View order ›</span>
              </button>
            )}
            {/* Spec 29-39: the Current Order as a mobile bottom sheet. At >=621
                this is not rendered at all, so there is exactly one order model
                at every width (spec 52). */}
            <TillOrderSheet open={orderExpanded} onOpenChange={setOrderExpanded} />
          </>
        ) : (
          <SelectedItems />
        )}
      </div>
    </CashierContext.Provider>
  );
};

export default Cashier;
